import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ScanLine, Loader2, Camera, Wand2, ChevronLeft, ChevronRight, CheckCircle2,
  Sparkles, Mic, Info, FileText, Rocket, Car, ShieldCheck, X, Lock, Trash2, RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import PublishTab from "@/components/vehicle/PublishTab";
import { PhotoAddButtons, UploadQueue } from "@/components/PhotoUploader";
import { api, apiError, fileToDataUrl } from "@/lib/api";
import { prepareImageForUpload } from "@/lib/image-utils";
import { useAuth } from "@/context/AuthContext";
import { useDictation } from "@/hooks/use-dictation";
import { usePhotoUpload } from "@/hooks/use-photo-upload";
import { FOTOBOOTH_TEMPLATES, FIELD_LABELS, fmtEUR } from "@/lib/constants";

const PHASES = ["Fahrzeug", "Fotos", "Angaben", "Inserat", "Veröffentlichung"];
const phaseOfStep = (step) => (step <= 1 ? 0 : step <= 3 ? 1 : step === 4 ? 2 : step === 5 ? 3 : 4);

const SHOW_FIELDS = ["marke", "modell", "variante", "baujahr", "erstzulassung", "leistung_ps", "leistung_kw", "kraftstoff", "getriebe", "antrieb", "karosserie", "farbe_aussen"];

export default function CaptureWizardPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = ["owner", "manager"].includes(user?.role);
  const resumeId = searchParams.get("vehicle");
  const [resumeLoading, setResumeLoading] = useState(Boolean(resumeId));
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState(null);

  // Schritt 0/1: FIN & Daten
  const [vin, setVin] = useState("");
  const [vinResult, setVinResult] = useState(null);
  const [vinErrors, setVinErrors] = useState([]);
  const [scanBusy, setScanBusy] = useState(false);

  // Fahrzeug (nach Anlage)
  const [vehicle, setVehicle] = useState(null);
  const [photos, setPhotos] = useState(null);

  // Schritt 3: Fotobooth
  const [template, setTemplate] = useState("studio_hell");
  const [windowsMode, setWindowsMode] = useState(true); // Studio-Hintergrund durch Scheiben fortsetzen
  const [batch, setBatch] = useState(null);

  // Schritt 4: Angaben
  const [km, setKm] = useState("");
  const [price, setPrice] = useState("");
  const [purchasePrice, setPurchasePrice] = useState(""); // Einkaufspreis – intern (nur Owner/Manager, optional)
  const [standort, setStandort] = useState("");
  const [availability, setAvailability] = useState("sofort");
  const [availDate, setAvailDate] = useState("");
  const [hints, setHints] = useState([]);
  const [note, setNote] = useState("");
  const [extractBusy, setExtractBusy] = useState(false);
  const [extractSummary, setExtractSummary] = useState(null);
  const [priceSuggestBusy, setPriceSuggestBusy] = useState(false);
  const [priceSuggestion, setPriceSuggestion] = useState(null);

  // Diktierfunktion (Browser-Diktat, Whisper automatisch sobald OpenAI-Key hinterlegt)
  const noteBaseRef = useRef("");
  const dictation = useDictation({
    onTranscript: (text) => setNote((noteBaseRef.current ? noteBaseRef.current.replace(/\s+$/, "") + " " : "") + text),
    onError: (msg) => toast.error(msg),
  });
  const toggleDictation = () => {
    if (dictation.status === "recording") { dictation.stop(); return; }
    if (dictation.status === "processing") return;
    noteBaseRef.current = note;
    dictation.start();
  };

  // Schritt 5: Inserat
  const [genBusy, setGenBusy] = useState(false);

  // FIN-Provider-Status (Live vs. Demo) - ohne Schluessel
  const [vinProvider, setVinProvider] = useState(null);

  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/vin/provider-status").then((r) => setVinProvider(r.data)).catch(() => {});
    api.get("/settings/branding").then((r) => {
      if (r.data.fotobooth_hintergrund) setTemplate(r.data.fotobooth_hintergrund);
    }).catch(() => {});
  }, []);

  const reloadVehicle = async () => {
    if (!vehicle) return;
    const r = await api.get(`/vehicles/${vehicle.id}`);
    setVehicle(r.data);
    return r.data;
  };

  const loadPhotos = async (vid) => {
    const r = await api.get(`/vehicles/${vid || vehicle.id}/photos`);
    setPhotos(r.data);
    return r.data;
  };

  // ---------- Aufnahme fortsetzen: bestehendes Fahrzeug laden & zum passenden Schritt springen ----------
  useEffect(() => {
    if (!resumeId) return;
    (async () => {
      try {
        const r = await api.get(`/vehicles/${resumeId}`);
        const v = r.data;
        setVehicle(v);
        setVin(v.vin || "");
        setStandort(v.specs?.standort || "");
        setKm(v.specs?.kilometerstand ?? "");
        setPrice(v.price ?? "");
        setPurchasePrice(v.purchase_price ?? "");
        const validAvail = ["sofort", "im_zulauf", "ab_datum", "besichtigung", "aufbereitung"];
        setAvailability(validAvail.includes(v.verfuegbarkeit) ? v.verfuegbarkeit : "sofort");
        setAvailDate(v.verfuegbar_ab || "");
        setHints(v.inserat_hinweise || []);
        const p = await loadPhotos(v.id);
        const stepParam = parseInt(searchParams.get("step") || "", 10);
        let target;
        if (!Number.isNaN(stepParam) && stepParam >= 2 && stepParam <= 6) {
          target = stepParam;
        } else {
          const imgs = p ? [...p.slots.flatMap((sl) => sl.images), ...(p.free_images || [])] : [];
          if (imgs.length === 0) target = 2;
          else if (v.price == null) target = 4;
          else if (!v.descriptions) target = 5;
          else target = 6;
        }
        setStep(target);
      } catch (e) {
        toast.error(apiError(e, "Fahrzeug konnte nicht geladen werden"));
        navigate("/app/fahrzeuge");
      } finally {
        setResumeLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, [resumeId]);

  // ---------- Schritt 0: FIN ----------
  const decode = async (vinValue) => {
    setBusy(true);
    setVinErrors([]);
    try {
      const r = await api.post("/vin/decode", { vin: vinValue });
      if (!r.data.valid) {
        setVinErrors(r.data.errors || ["Ungültige FIN"]);
        return;
      }
      setVinResult(r.data);
      setVin(r.data.vin);
      setStep(1);
    } catch (e) {
      toast.error(apiError(e, "FIN konnte nicht geprüft werden"));
    } finally {
      setBusy(false);
    }
  };

  const loadDemo = async () => {
    setBusy(true);
    try {
      const r = await api.get("/vin/demo");
      setVin(r.data.demo_vin);
      await decode(r.data.demo_vin);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  // FIN per Kamera-Foto erkennen (Fahrzeugschein Feld E / VIN-Plakette)
  const scanDocument = async (file) => {
    setScanBusy(true);
    setVinErrors([]);
    try {
      const dataUrl = await fileToDataUrl(file);
      const r = await api.post("/vin/scan", { data_url: dataUrl });
      if (!r.data.found) {
        toast.error(r.data.message || "Keine FIN erkannt – bitte erneut fotografieren.");
        return;
      }
      setVin(r.data.vin);
      if (!r.data.valid) {
        setVinErrors(r.data.errors || []);
        toast.warning("FIN erkannt, aber ungültig – bitte prüfen und korrigieren.");
        return;
      }
      toast.success(`FIN erkannt (${r.data.gefunden_in || "Dokument"}, Vertrauen: ${r.data.confidence})`);
      await decode(r.data.vin);
    } catch (e) {
      toast.error(apiError(e, "Scan fehlgeschlagen – bitte erneut versuchen oder FIN eintippen."));
    } finally {
      setScanBusy(false);
    }
  };

  // ---------- Schritt 1 -> Fahrzeug anlegen ----------
  const createVehicle = async (force = false) => {
    setBusy(true);
    try {
      const r = await api.post("/vehicles", { vin, use_decoded: true, force_duplicate: force });
      setVehicle(r.data);
      setStandort(r.data.specs?.standort || "");
      setKm(r.data.specs?.kilometerstand ?? "");
      await loadPhotos(r.data.id);
      setStep(2);
    } catch (e) {
      toast.error(apiError(e, "Fahrzeug konnte nicht angelegt werden"));
    } finally {
      setBusy(false);
    }
  };

  // ---------- Schritt 2: Fotos ----------
  // Upload-Queue: Fortschritt pro Bild, Einzel-Retry, HEIC-Unterstützung
  const uploader = usePhotoUpload(vehicle?.id, { onUploaded: () => loadPhotos() });

  const deletePhoto = async (imageId) => {
    try {
      await api.delete(`/vehicles/${vehicle.id}/photos/${imageId}`);
      await loadPhotos();
    } catch (e) { toast.error(apiError(e, "Löschen fehlgeschlagen")); }
  };

  const replacePhoto = async (imageId, file) => {
    try {
      const prep = await prepareImageForUpload(file);
      await api.post(`/vehicles/${vehicle.id}/photos/${imageId}/replace`, { data_url: prep.dataUrl, filename: prep.filename });
      await loadPhotos();
      toast.success("Bild ersetzt");
    } catch (e) { toast.error(apiError(e, "Ersetzen fehlgeschlagen")); }
  };

  const assignSlot = async (imageId, slotKey) => {
    try {
      await api.patch(`/vehicles/${vehicle.id}/photos/${imageId}/slot`, { slot: slotKey || null });
      await loadPhotos();
    } catch (e) { toast.error(apiError(e, "Zuordnung fehlgeschlagen")); }
  };

  // ---------- Schritt 3: Fotobooth ----------
  const allImages = useMemo(() => {
    if (!photos) return [];
    const slotImgs = photos.slots.flatMap((s) => s.images);
    return [...slotImgs, ...(photos.free_images || [])];
  }, [photos]);
  const editableImages = allImages.filter((i) => (i.original_url || i.url || "").startsWith("data:"));

  // Alle Fotos AUF EINMAL durch die Fotobooth (parallel, max. 4 gleichzeitig)
  const runFotobooth = async () => {
    if (editableImages.length === 0) {
      toast.info("Keine eigenen Foto-Uploads vorhanden – dieser Schritt kann übersprungen werden.");
      return;
    }
    setBatch({ done: 0, total: editableImages.length });
    let ok = 0;
    let done = 0;
    const CONCURRENCY = 4;
    const queue = [...editableImages];

    const worker = async () => {
      while (queue.length > 0) {
        const img = queue.shift();
        try {
          await api.post(`/vehicles/${vehicle.id}/photos/${img.id}/fotobooth`, { template, blur_plate: true, apply_branding: true, fenster_fortsetzen: windowsMode });
          ok++;
        } catch (e) { /* Original bleibt erhalten */ }
        done++;
        setBatch({ done, total: editableImages.length });
        loadPhotos();
      }
    };

    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, editableImages.length) }, worker));
    setBatch(null);
    await loadPhotos();
    await reloadVehicle();
    toast.success(`Fotobooth: ${ok} von ${editableImages.length} Bildern in Studio-Qualität`);
  };

  // ---------- Schritt 4: Angaben ----------
  const structureNote = async () => {
    if (!note.trim()) return;
    setExtractBusy(true);
    try {
      const r = await api.post(`/vehicles/${vehicle.id}/notes`, { text: note, type: "text" });
      const ext = r.data.extraction;
      if (!ext) {
        toast.error(r.data.extraction_error || "KI-Extraktion nicht verfügbar – Notiz wurde gespeichert");
        return;
      }
      const apply = {
        field_indexes: (ext.extracted_fields || []).map((_, i) => i),
        claim_indexes: (ext.sensitive_claims || []).map((_, i) => i),
        damage_indexes: (ext.damages || []).map((_, i) => i),
        task_indexes: [],
      };
      await api.post(`/vehicles/${vehicle.id}/notes/${r.data.id}/apply`, apply);
      setExtractSummary({
        fields: (ext.extracted_fields || []).length,
        claims: (ext.sensitive_claims || []).length,
        damages: (ext.damages || []).length,
      });
      await reloadVehicle();
      toast.success("Hinweise übernommen – sensible Angaben müssen vor dem Inserat bestätigt werden");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setExtractBusy(false);
    }
  };

  const saveDetails = async () => {
    setBusy(true);
    try {
      const specs = {};
      if (km !== "" && km != null) specs.kilometerstand = parseInt(km, 10);
      if (standort) specs.standort = standort;
      await api.patch(`/vehicles/${vehicle.id}`, {
        specs: Object.keys(specs).length ? specs : undefined,
        price: price !== "" ? parseFloat(price) : undefined,
        verfuegbarkeit: availability,
        verfuegbar_ab: availability === "ab_datum" ? availDate || null : null,
        inserat_hinweise: hints,
        source: "manuell",
      });
      // Einkaufspreis (intern, optional) – blockiert den Flow NIEMALS
      if (isAdmin && purchasePrice !== "" && purchasePrice != null && parseFloat(purchasePrice) !== vehicle.purchase_price) {
        try {
          await api.patch(`/vehicles/${vehicle.id}/purchase-price`, { purchase_price: parseFloat(purchasePrice) });
        } catch (e) {
          toast.warning("Einkaufspreis konnte nicht gespeichert werden – Sie können ihn später im Tab „Markt & Marge“ ergänzen.");
        }
      }
      await reloadVehicle();
      setStep(5);
    } catch (e) {
      toast.error(apiError(e, "Angaben konnten nicht gespeichert werden"));
    } finally {
      setBusy(false);
    }
  };

  // KI-Preisvorschlag (Schätzung ohne Live-Marktdaten)
  const suggestPrice = async () => {
    setPriceSuggestBusy(true);
    try {
      const body = km !== "" && km != null ? { kilometerstand: parseInt(km, 10) } : {};
      const r = await api.post(`/vehicles/${vehicle.id}/price-suggestion`, body);
      setPriceSuggestion(r.data);
    } catch (e) {
      toast.error(apiError(e, "Der KI-Preisvorschlag ist momentan nicht verfügbar"));
    } finally {
      setPriceSuggestBusy(false);
    }
  };

  // ---------- Schritt 5: Inserat ----------
  const [regenConfirm, setRegenConfirm] = useState(null); // null | { style }
  const generate = async (style = null) => {
    setGenBusy(true);
    try {
      await api.post(`/vehicles/${vehicle.id}/descriptions/generate`, style ? { style } : {});
      await reloadVehicle();
      toast.success("Inserat erstellt – nur bestätigte Angaben wurden verwendet");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setGenBusy(false);
    }
  };

  // Warnung, bevor manuell bearbeitete Texte durch die KI ersetzt werden
  const requestGenerate = (style = null) => {
    if (vehicle?.descriptions?.edited_at) setRegenConfirm({ style });
    else generate(style);
  };

  const confirmClaim = async (claimId, ok) => {
    try {
      await api.post(`/vehicles/${vehicle.id}/claims/${claimId}/${ok ? "confirm" : "reject"}`);
      await reloadVehicle();
      toast.success(ok ? "Angabe bestätigt" : "Angabe wird nicht verwendet");
    } catch (e) { toast.error(apiError(e)); }
  };

  const editDescription = async (field, value) => {
    try {
      await api.patch(`/vehicles/${vehicle.id}/descriptions`, { updates: { [field]: value } });
      await reloadVehicle();
      toast.success("Text aktualisiert");
    } catch (e) { toast.error(apiError(e)); }
  };

  // ================= RENDER =================
  const phase = phaseOfStep(step);
  const unconfirmedClaims = (vehicle?.claims || []).filter((c) => c.requires_confirmation && c.confirmation_status === "unconfirmed");
  const d = vehicle?.descriptions;

  if (resumeLoading)
    return (
      <div className="max-w-3xl mx-auto space-y-4 pt-6" data-testid="wizard-resume-loading">
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Aufnahme wird fortgesetzt...</div>
        <div className="h-40 rounded-2xl bg-secondary/50 animate-pulse" />
        <div className="h-64 rounded-2xl bg-secondary/40 animate-pulse" />
      </div>
    );

  return (
    <div className="max-w-3xl mx-auto space-y-5 pb-16">
      {/* Fortschritt */}
      <div>
        <h1 className="font-display text-2xl font-bold tracking-[-0.02em]">{resumeId ? "Aufnahme fortsetzen" : "Fahrzeug hinzufügen"}</h1>
        <div className="flex items-center gap-1.5 mt-3" data-testid="wizard-progress">
          {PHASES.map((p, i) => (
            <React.Fragment key={p}>
              <div className={`flex items-center gap-1.5 ${i <= phase ? "text-primary" : "text-muted-foreground"}`} data-tour={`wizard-phase-${i}`}>
                <div className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold border ${i < phase ? "bg-primary text-primary-foreground border-primary" : i === phase ? "border-primary text-primary" : "border-border"}`}>
                  {i < phase ? <CheckCircle2 className="h-3.5 w-3.5" /> : i + 1}
                </div>
                <span className="text-xs font-medium hidden sm:inline">{p}</span>
              </div>
              {i < PHASES.length - 1 && <div className={`h-px flex-1 ${i < phase ? "bg-primary/60" : "bg-border"}`} />}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* ---------- Schritt 0: FIN ---------- */}
      {step === 0 && (
        <Card data-testid="wizard-step-vin" data-tour="wizard-vin">
          <CardHeader>
            <CardTitle className="text-lg font-display flex items-center gap-2"><ScanLine className="h-5 w-5 text-primary" /> FIN erfassen</CardTitle>
            <p className="text-sm text-muted-foreground">Fahrgestellnummer eingeben – die Fahrzeugdaten werden automatisch geladen.</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="vin-input">Fahrgestellnummer (FIN)</Label>
              <Input
                id="vin-input"
                value={vin}
                onChange={(e) => setVin(e.target.value.toUpperCase())}
                placeholder="z.B. WBA8E11070K123456"
                className="font-mono tracking-wider h-12 text-base"
                maxLength={17}
                data-testid="wizard-vin-input"
              />
              {vinErrors.map((err, i) => (
                <p key={i} className="text-xs text-red-300" data-testid="wizard-vin-error">{err}</p>
              ))}
            </div>
            <Button className="w-full h-12 gap-2 font-semibold" disabled={busy || vin.length < 11} onClick={() => decode(vin)} data-testid="wizard-vin-decode-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScanLine className="h-4 w-4" />} Fahrzeugdaten laden
            </Button>
            <div className="grid sm:grid-cols-2 gap-2">
              <Button variant="outline" className="h-11 gap-2" disabled={busy} onClick={loadDemo} data-testid="wizard-demo-button">
                <Car className="h-4 w-4 text-primary" /> Demo-Fahrzeug laden
              </Button>
              <label className="inline-flex">
                <input type="file" accept="image/*" capture="environment" className="hidden"
                  onChange={(e) => e.target.files?.[0] && scanDocument(e.target.files[0])} data-testid="wizard-scan-input" />
                <Button variant="outline" className="h-11 gap-2 w-full pointer-events-none" disabled={busy || scanBusy} data-testid="wizard-scan-button" asChild>
                  <span>
                    {scanBusy ? <Loader2 className="h-4 w-4 animate-spin text-primary" /> : <Camera className="h-4 w-4 text-primary" />}
                    {scanBusy ? "Dokument wird gelesen..." : "FIN / Fahrzeugschein scannen"}
                  </span>
                </Button>
              </label>
            </div>
            <p className="text-[11px] text-muted-foreground flex items-start gap-1.5">
              <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              Scan: Feld E des Fahrzeugscheins oder die VIN-Plakette gut ausgeleuchtet fotografieren – die KI liest die FIN automatisch. Demo-Fahrzeug: BMW 320d Touring (2021) als Demo-Datensatz – es erfolgt keine echte FIN-Abfrage.
            </p>
            {vinProvider && (
              <div
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${vinProvider.live ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-sky-500/30 bg-sky-500/10 text-sky-300"}`}
                data-testid="wizard-vin-provider-status"
              >
                <span className={`h-1.5 w-1.5 rounded-full ${vinProvider.live ? "bg-emerald-400" : "bg-sky-400"}`} />
                {vinProvider.live ? `Live-FIN-Abfrage aktiv · ${vinProvider.name}` : "Demo-Modus · kein Live-Datenanbieter verbunden"}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ---------- Schritt 1: Daten pruefen ---------- */}
      {step === 1 && vinResult && (
        <Card data-testid="wizard-step-review">
          <CardHeader>
            <CardTitle className="text-lg font-display flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-400" /> Fahrzeug erkannt: {vinResult.decoded.marke} {vinResult.decoded.modell}
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Quelle: {vinResult.provider}{vinResult.is_demo && <span className="ml-2 rounded-full bg-sky-500/15 text-sky-300 border border-sky-500/30 px-2 py-0.5 text-[10px] font-semibold">Demo-Datensatz – keine echte FIN-Abfrage</span>}
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {vinResult.duplicate && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-300" data-testid="wizard-duplicate-warning">
                Diese FIN existiert bereits im Bestand ({vinResult.existing_vehicle?.bestandsnummer}). Sie können trotzdem fortfahren.
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3 text-sm">
              {SHOW_FIELDS.map((k) =>
                vinResult.decoded[k] != null ? (
                  <div key={k}>
                    <div className="text-xs text-muted-foreground">{FIELD_LABELS[k] || k}</div>
                    <div className="font-medium">{vinResult.decoded[k]}</div>
                  </div>
                ) : null
              )}
            </div>
            {(vinResult.equipment || []).length > 0 && (
              <div>
                <div className="text-xs text-muted-foreground mb-1.5">Ausstattung ({vinResult.equipment.length})</div>
                <div className="flex flex-wrap gap-1.5">
                  {vinResult.equipment.slice(0, 10).map((e) => (
                    <span key={e} className="rounded-full border border-border bg-secondary/50 px-2 py-0.5 text-xs">{e}</span>
                  ))}
                  {vinResult.equipment.length > 10 && <span className="text-xs text-muted-foreground">+{vinResult.equipment.length - 10} weitere</span>}
                </div>
              </div>
            )}
            <Button className="w-full h-12 gap-2 font-semibold" disabled={busy} onClick={() => createVehicle(vinResult.duplicate)} data-testid="wizard-create-vehicle-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronRight className="h-4 w-4" />} Daten übernehmen & Fotos aufnehmen
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ---------- Schritt 2: Fotos ---------- */}
      {step === 2 && vehicle && photos && (
        <Card data-testid="wizard-step-photos">
          <CardHeader>
            <CardTitle className="text-lg font-display flex items-center gap-2"><Camera className="h-5 w-5 text-primary" /> Fotos hinzufügen</CardTitle>
            <p className="text-sm text-muted-foreground">Direkt mit dem Smartphone fotografieren oder vorhandene Bilder aus der Galerie verwenden. Egal wo das Fahrzeug steht – die Fotobooth macht daraus Studio-Bilder.</p>
            {photos.total > 0 && photos.total < 12 && (
              <p className="text-xs text-sky-300 flex items-center gap-1.5" data-testid="wizard-photo-recommendation">
                <Info className="h-3.5 w-3.5" /> {photos.total} Foto{photos.total === 1 ? "" : "s"} vorhanden. Für ein optimales Inserat empfehlen wir mindestens 12 – Sie können trotzdem fortfahren.
              </p>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <PhotoAddButtons onFiles={(files) => uploader.addFiles(files, null)} testPrefix="wizard-photo-add" />
            <UploadQueue items={uploader.items} onRetry={uploader.retry} onDismiss={uploader.dismiss} testPrefix="wizard-photo-queue" />

            <div>
              <p className="text-xs text-muted-foreground mb-2">Perspektiven – tippen für Kamera oder Galerie:</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5" data-testid="wizard-photo-grid">
                {photos.slots.slice(0, 16).map((slot) => {
                  const img = slot.images[0];
                  return (
                    <div key={slot.key} className={`relative rounded-lg border overflow-hidden ${img ? "border-border" : "border-dashed border-border bg-secondary/30"}`}>
                      {img ? (
                        <>
                          <img src={img.url} alt={slot.label} className="aspect-square w-full object-cover" loading="lazy" />
                          <div className="absolute inset-x-0 bottom-0 flex items-center justify-end gap-1 bg-gradient-to-t from-black/70 to-transparent p-1">
                            <label className="rounded bg-black/50 p-1 cursor-pointer hover:bg-black/70" title="Bild ersetzen">
                              {/* Ersetzen: native Auswahl (Kamera ODER Galerie) */}
                              <input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif,image/*" className="hidden"
                                onChange={(e) => { if (e.target.files?.[0]) replacePhoto(img.id, e.target.files[0]); e.target.value = ""; }}
                                data-testid={`wizard-photo-replace-${slot.key}`} />
                              <RefreshCw className="h-3 w-3 text-white" />
                            </label>
                            <button type="button" className="rounded bg-black/50 p-1 hover:bg-red-500/80" title="Bild löschen"
                              onClick={() => deletePhoto(img.id)} data-testid={`wizard-photo-delete-${slot.key}`}>
                              <Trash2 className="h-3 w-3 text-white" />
                            </button>
                          </div>
                        </>
                      ) : (
                        <label className="flex flex-col items-center justify-center aspect-square cursor-pointer hover:bg-secondary/60 transition-colors p-1 text-center">
                          {/* Bewusst OHNE capture: das Gerät zeigt die native Auswahl (Kamera oder Galerie) */}
                          <input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif,image/*" className="hidden"
                            onChange={(e) => { if (e.target.files?.[0]) uploader.addFiles([e.target.files[0]], slot.key); e.target.value = ""; }}
                            data-testid={`wizard-photo-upload-${slot.key}`} />
                          <Camera className="h-4 w-4 text-muted-foreground mb-1" />
                          <span className="text-[9px] text-muted-foreground leading-tight">{slot.label}</span>
                        </label>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {(photos.free_images || []).length > 0 && (
              <div data-testid="wizard-free-photos">
                <p className="text-xs text-muted-foreground mb-2">
                  Weitere Fotos ({photos.free_images.length}) – optional einer Perspektive zuordnen:
                </p>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
                  {photos.free_images.map((img) => {
                    const emptySlots = photos.slots.filter((s) => s.images.length === 0);
                    return (
                      <div key={img.id} className="space-y-1" data-testid="wizard-free-photo">
                        <div className="relative rounded-lg border border-border overflow-hidden">
                          <img src={img.url} alt="" className="aspect-square w-full object-cover" loading="lazy" />
                          <button type="button" className="absolute top-1 right-1 rounded bg-black/50 p-1 hover:bg-red-500/80" title="Bild löschen"
                            onClick={() => deletePhoto(img.id)} data-testid="wizard-free-photo-delete">
                            <Trash2 className="h-3 w-3 text-white" />
                          </button>
                        </div>
                        {emptySlots.length > 0 && (
                          <Select value="" onValueChange={(slotKey) => assignSlot(img.id, slotKey)}>
                            <SelectTrigger className="h-7 text-[10px]" data-testid="wizard-free-photo-assign">
                              <SelectValue placeholder="Zuordnen..." />
                            </SelectTrigger>
                            <SelectContent>
                              {emptySlots.map((s) => (
                                <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ---------- Schritt 3: Fotobooth ---------- */}
      {step === 3 && vehicle && photos && (
        <Card data-testid="wizard-step-fotobooth">
          <CardHeader>
            <CardTitle className="text-lg font-display flex items-center gap-2"><Wand2 className="h-5 w-5 text-primary" /> Fotobooth anwenden</CardTitle>
            <p className="text-sm text-muted-foreground">Hintergrund wählen – die KI erstellt professionelle Studio-Bilder. Das Fahrzeug bleibt 100% unverändert.</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {FOTOBOOTH_TEMPLATES.map((tpl) => (
                <button key={tpl.key} onClick={() => setTemplate(tpl.key)}
                  className={`rounded-xl border p-2 text-left transition-colors ${template === tpl.key ? "border-primary ring-1 ring-primary/50" : "border-border hover:border-primary/40"}`}
                  data-testid={`wizard-template-${tpl.key}`}>
                  <div className="h-8 w-full rounded-md" style={{ background: tpl.swatch }} />
                  <div className="text-[11px] font-medium mt-1">{tpl.label}</div>
                </button>
              ))}
            </div>
            <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-secondary/30 p-3">
              <div className="min-w-0">
                <Label htmlFor="wizard-windows-mode" className="text-sm">Studio-Hintergrund durch Scheiben fortsetzen</Label>
                <p className="text-[10px] text-muted-foreground">Ersetzt auch den alten Aufnahmeort hinter Front-, Seiten- und Heckscheiben. Innenraum & Scheibentönung bleiben unverändert.</p>
              </div>
              <Switch id="wizard-windows-mode" checked={windowsMode} onCheckedChange={setWindowsMode} data-testid="wizard-windows-switch" />
            </div>
            {editableImages.length > 0 ? (
              <Button className="w-full h-12 gap-2 font-semibold" disabled={Boolean(batch)} onClick={runFotobooth} data-testid="wizard-fotobooth-run-button">
                {batch ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                {batch ? `KI bearbeitet alle Fotos parallel – ${batch.done} von ${batch.total} fertig...` : `Studio-Look auf alle ${editableImages.length} Foto(s) anwenden`}
              </Button>
            ) : (
              <div className="rounded-lg border border-border bg-secondary/30 p-3 text-xs text-muted-foreground" data-testid="wizard-fotobooth-no-images">
                Keine eigenen Foto-Uploads vorhanden. Dieser Schritt kann übersprungen und später in der Fotobooth nachgeholt werden.
              </div>
            )}
            {batch && <Progress value={(batch.done / batch.total) * 100} className="h-1.5" />}
            {allImages.some((i) => i.processing_status === "processed") && (
              <div className="grid grid-cols-4 gap-2">
                {allImages.filter((i) => i.processing_status === "processed").slice(0, 8).map((i) => (
                  <div key={i.id} className="relative rounded-lg overflow-hidden border border-emerald-500/40">
                    <img src={i.url} alt="" className="aspect-square w-full object-cover" />
                    <span className="absolute top-1 right-1 rounded-full bg-emerald-500/90 text-black text-[8px] font-bold px-1">STUDIO</span>
                  </div>
                ))}
              </div>
            )}
            <p className="text-[11px] text-muted-foreground">
              Feinschliff pro Bild (Vorher/Nachher, Original behalten, neu generieren) jederzeit in der <button className="text-primary underline" onClick={() => navigate(`/app/fotobooth?vehicle=${vehicle.id}`)} data-testid="wizard-goto-fotobooth">Fotobooth</button>.
            </p>
          </CardContent>
        </Card>
      )}

      {/* ---------- Schritt 4: Angaben ---------- */}
      {step === 4 && vehicle && meta && (
        <Card data-testid="wizard-step-details">
          <CardHeader>
            <CardTitle className="text-lg font-display flex items-center gap-2"><FileText className="h-5 w-5 text-primary" /> Angaben ergänzen</CardTitle>
            <p className="text-sm text-muted-foreground">Nur das Wichtigste – Sie legen den Preis selbst fest.</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="wizard-km">Kilometerstand *</Label>
                <Input id="wizard-km" type="number" value={km} onChange={(e) => setKm(e.target.value)} placeholder="z.B. 68500" className="h-11" data-testid="wizard-km-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="wizard-price">Verkaufspreis (EUR) *</Label>
                <Input id="wizard-price" type="number" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="z.B. 28900" className="h-11" data-testid="wizard-price-input" />
                {isAdmin && (
                  <div className="rounded-lg border border-dashed border-border bg-secondary/20 p-2.5 space-y-1 mt-1.5" data-testid="wizard-purchase-price-box">
                    <Label htmlFor="wizard-purchase-price" className="text-xs text-muted-foreground flex items-center gap-1.5">
                      <Lock className="h-3 w-3 text-primary" /> Einkaufspreis – intern <span className="text-[10px]">(optional)</span>
                    </Label>
                    <Input id="wizard-purchase-price" type="number" min="0" value={purchasePrice}
                      onChange={(e) => setPurchasePrice(e.target.value)} placeholder="z.B. 21500"
                      className="h-10" data-testid="wizard-purchase-price-input" />
                    <p className="text-[10px] text-muted-foreground">
                      Wird niemals veröffentlicht und dient ausschließlich der Margen- und späteren DealAlert-Analyse.
                    </p>
                  </div>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="wizard-standort">Standort</Label>
                <Input id="wizard-standort" value={standort} onChange={(e) => setStandort(e.target.value)} placeholder="z.B. München" className="h-11" data-testid="wizard-standort-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Verfügbarkeit</Label>
                <Select value={availability} onValueChange={setAvailability}>
                  <SelectTrigger className="h-11" data-testid="wizard-availability-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(meta.availability_options || []).map((o) => (
                      <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {availability === "ab_datum" && (
                <div className="space-y-1.5">
                  <Label htmlFor="wizard-avail-date">Verfügbar ab</Label>
                  <Input id="wizard-avail-date" type="date" value={availDate} onChange={(e) => setAvailDate(e.target.value)} className="h-11" data-testid="wizard-avail-date-input" />
                </div>
              )}
            </div>

            {/* KI-Preisvorschlag (Schätzung ohne Live-Marktdaten) */}
            <div className="rounded-lg border border-primary/25 bg-primary/5 p-3 space-y-3" data-testid="wizard-price-suggestion-box">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-sm font-medium flex items-center gap-1.5">
                  <Sparkles className="h-4 w-4 text-primary" /> Unsicher beim Preis?
                  <span className="rounded-full border border-primary/30 bg-primary/10 text-primary text-[10px] px-2 py-0.5">KI-Schätzung</span>
                </div>
                <Button type="button" size="sm" variant="outline" className="gap-1.5 border-primary/40 text-primary hover:bg-primary/10"
                  disabled={priceSuggestBusy} onClick={suggestPrice} data-testid="wizard-price-suggest-button">
                  {priceSuggestBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                  {priceSuggestBusy ? "KI analysiert Fahrzeugdaten..." : priceSuggestion ? "Neu berechnen" : "KI-Preisvorschlag"}
                </Button>
              </div>

              {!priceSuggestion && !priceSuggestBusy && (
                <p className="text-xs text-muted-foreground">
                  Die KI gibt eine Preisorientierung auf Basis von Marke, Modell, Baujahr, Kilometerstand, Ausstattung und Zustand.
                </p>
              )}

              {priceSuggestBusy && (
                <div className="space-y-2">
                  <div className="h-6 w-48 rounded bg-secondary/60 animate-pulse" />
                  <div className="h-3 w-full rounded bg-secondary/60 animate-pulse" />
                  <div className="h-3 w-2/3 rounded bg-secondary/60 animate-pulse" />
                </div>
              )}

              {priceSuggestion && !priceSuggestBusy && (
                <div className="space-y-2.5" data-testid="wizard-price-suggestion-result">
                  <div className="flex items-end gap-5 flex-wrap">
                    <div>
                      <div className="text-[11px] text-muted-foreground">Preisspanne</div>
                      <div className="font-display text-lg font-bold tabular-nums" data-testid="wizard-price-suggestion-range">
                        {fmtEUR(priceSuggestion.preis_von)} – {fmtEUR(priceSuggestion.preis_bis)}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] text-muted-foreground">Empfehlung</div>
                      <div className="font-display text-lg font-bold text-primary tabular-nums" data-testid="wizard-price-suggestion-recommended">
                        {fmtEUR(priceSuggestion.empfohlener_preis)}
                      </div>
                    </div>
                    <Button type="button" size="sm" className="gap-1.5"
                      onClick={() => { setPrice(String(priceSuggestion.empfohlener_preis)); toast.success(`Preisvorschlag übernommen: ${fmtEUR(priceSuggestion.empfohlener_preis)}`); }}
                      data-testid="wizard-price-suggestion-apply">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Übernehmen
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground" data-testid="wizard-price-suggestion-reason">{priceSuggestion.begruendung}</p>
                  {(priceSuggestion.einflussfaktoren || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {priceSuggestion.einflussfaktoren.slice(0, 6).map((f, i) => (
                        <span key={i} className={`rounded-full border px-2 py-0.5 text-[10px] ${
                          f.wirkung === "positiv" ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                          : f.wirkung === "negativ" ? "border-amber-500/30 bg-amber-500/10 text-amber-300"
                          : "border-border bg-secondary/40 text-muted-foreground"}`}>
                          {f.faktor}
                        </span>
                      ))}
                    </div>
                  )}
                  <p className="text-[10px] text-muted-foreground flex items-start gap-1" data-testid="wizard-price-suggestion-disclaimer">
                    <Info className="h-3 w-3 mt-0.5 shrink-0" /> {priceSuggestion.disclaimer}
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>Inserats-Hinweise (Pre-Listing)</Label>
              <div className="flex flex-wrap gap-1.5">
                {(meta.listing_hints || []).map((h) => {
                  const active = hints.includes(h.label);
                  return (
                    <button key={h.key} onClick={() => setHints((prev) => active ? prev.filter((x) => x !== h.label) : [...prev, h.label])}
                      className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${active ? "border-primary bg-primary/15 text-primary" : "border-border bg-secondary/40 text-muted-foreground hover:border-primary/40"}`}
                      data-testid={`wizard-hint-${h.key}`}>
                      {h.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="wizard-note">Hinweise (diktieren oder tippen)</Label>
                <Button variant="outline" size="sm"
                  className={`h-7 gap-1.5 text-xs ${dictation.status === "recording" ? "border-red-500/50 text-red-300 hover:bg-red-500/10" : ""}`}
                  onClick={toggleDictation} disabled={dictation.status === "processing"}
                  data-testid="wizard-voice-button">
                  {dictation.status === "processing"
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <Mic className={`h-3.5 w-3.5 ${dictation.status === "recording" ? "animate-pulse text-red-400" : ""}`} />}
                  {dictation.status === "recording" ? "Aufnahme stoppen" : dictation.status === "processing" ? "Transkribiere..." : "Diktieren"}
                </Button>
              </div>
              <Textarea id="wizard-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                placeholder='z.B. "Zwei Schlüssel, Service neu, sehr gepflegt, kleiner Kratzer hinten rechts, Winterräder dabei"'
                data-testid="wizard-note-textarea" />
              {dictation.status === "recording" && (
                <p className="text-[11px] text-red-300 flex items-center gap-1" data-testid="wizard-dictation-recording-hint">
                  <span className="h-1.5 w-1.5 rounded-full bg-red-400 animate-pulse" /> Aufnahme läuft – einfach sprechen, der Text erscheint im Feld.
                </p>
              )}
              {dictation.mode === "browser" && dictation.status === "idle" && (
                <p className="text-[10px] text-muted-foreground" data-testid="wizard-dictation-mode-hint">
                  Diktat über den Browser (Chrome, Edge, Safari) – kostenlos. Whisper-Qualität aktiviert sich automatisch, sobald ein OpenAI-Key hinterlegt ist.
                </p>
              )}
              <div className="flex flex-wrap gap-1.5 pt-1">
                {(meta.quick_chips || []).slice(0, 10).map((c) => (
                  <button key={c.key || c.label} onClick={() => setNote((n) => (n ? n + ", " : "") + c.label)}
                    className="rounded-full border border-border bg-secondary/40 px-2.5 py-1 text-xs text-muted-foreground hover:border-primary/40 hover:text-foreground transition-colors"
                    data-testid={`wizard-chip-${c.key || c.label}`}>
                    + {c.label}
                  </button>
                ))}
              </div>
              <Button variant="secondary" size="sm" className="gap-1.5 mt-1" disabled={extractBusy || !note.trim()} onClick={structureNote} data-testid="wizard-extract-button">
                {extractBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} KI strukturieren & übernehmen
              </Button>
              {extractSummary && (
                <p className="text-xs text-emerald-300" data-testid="wizard-extract-summary">
                  Übernommen: {extractSummary.fields} Feld(er), {extractSummary.claims} sensible Angabe(n), {extractSummary.damages} Schaden/Schäden
                </p>
              )}
            </div>

            <Collapsible>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="text-xs text-muted-foreground gap-1" data-testid="wizard-more-fields-toggle">Weitere Angaben (optional) <ChevronRight className="h-3 w-3" /></Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-2">
                <p className="text-xs text-muted-foreground">Vorbesitzer, Schlüssel, Garantie, MwSt., Unfallstatus u.v.m. können jederzeit im Fahrzeug unter „Fahrzeugdaten“ ergänzt werden – auch nach der Veröffentlichung.</p>
              </CollapsibleContent>
            </Collapsible>

            <Button className="w-full h-12 gap-2 font-semibold" disabled={busy} onClick={saveDetails} data-testid="wizard-save-details-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronRight className="h-4 w-4" />} Speichern & Inserat erstellen
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ---------- Schritt 5: Inserat ---------- */}
      {step === 5 && vehicle && (
        <div className="space-y-4" data-testid="wizard-step-listing">
          {unconfirmedClaims.length > 0 && (
            <Card className="border-amber-500/40" data-testid="wizard-claims-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-display flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-amber-300" /> Bitte bestätigen</CardTitle>
                <p className="text-xs text-muted-foreground">Rechtlich sensible Angaben erscheinen nur nach Ihrer Bestätigung im Inserat.</p>
              </CardHeader>
              <CardContent className="space-y-2">
                {unconfirmedClaims.map((c) => (
                  <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-secondary/30 p-2.5" data-testid={`wizard-claim-${c.id}`}>
                    <span className="text-sm">Das Fahrzeug darf als <b>„{c.claim_value}“</b> beschrieben werden?</span>
                    <div className="flex gap-1.5">
                      <Button size="sm" className="h-8 text-xs gap-1" onClick={() => confirmClaim(c.id, true)} data-testid={`wizard-claim-confirm-${c.id}`}><CheckCircle2 className="h-3.5 w-3.5" /> Bestätigen</Button>
                      <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => confirmClaim(c.id, false)} data-testid={`wizard-claim-reject-${c.id}`}><X className="h-3.5 w-3.5" /> Nicht verwenden</Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-lg font-display flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" /> KI-Inserat</CardTitle>
                <Button onClick={() => requestGenerate()} disabled={genBusy} className="gap-2 font-semibold" data-testid="wizard-generate-button">
                  {genBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  {d ? "Neu generieren" : "Inserat erstellen"}
                </Button>
              </div>
              {d && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {[["sachlicher", "Sachlicher"], ["emotionaler", "Emotionaler"], ["kuerzer", "Kürzen"], ["premium", "Premium-Stil"]].map(([key, label]) => (
                    <Button key={key} variant="outline" size="sm" className="h-7 text-xs" disabled={genBusy} onClick={() => requestGenerate(key)} data-testid={`wizard-style-${key}`}>{label}</Button>
                  ))}
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              {genBusy && !d && <p className="text-sm text-muted-foreground animate-pulse py-6 text-center">Die KI erstellt Titel, Beschreibung und Highlights...</p>}
              {!genBusy && !d && <p className="text-sm text-muted-foreground py-6 text-center">Noch kein Inserat – klicken Sie auf „Inserat erstellen“.</p>}
              {d && (
                <div className="space-y-3" data-testid="wizard-listing-preview">
                  <EditableBlock label="Börsentitel" value={d.boersen_titel} onSave={(v) => editDescription("boersen_titel", v)} testId="wizard-desc-titel" rows={2} />
                  <EditableBlock label="Kurzbeschreibung" value={d.kurzbeschreibung} onSave={(v) => editDescription("kurzbeschreibung", v)} testId="wizard-desc-kurz" rows={3} />
                  <EditableBlock label="Beschreibung" value={d.beschreibung} onSave={(v) => editDescription("beschreibung", v)} testId="wizard-desc-lang" rows={8} />
                  {(d.ausstattung_highlights || []).length > 0 && (
                    <div>
                      <div className="text-xs text-muted-foreground mb-1.5">Highlights</div>
                      <div className="flex flex-wrap gap-1.5">
                        {d.ausstattung_highlights.map((h, i) => (
                          <span key={i} className="rounded-full border border-primary/30 bg-primary/10 text-primary px-2.5 py-1 text-xs">{h}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  <Button className="w-full h-12 gap-2 font-semibold" onClick={() => setStep(6)} data-testid="wizard-to-publish-button">
                    <Rocket className="h-4 w-4" /> Weiter zur Veröffentlichung
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ---------- Schritt 6: Veroeffentlichung ---------- */}
      {step === 6 && vehicle && (
        <div className="space-y-4" data-testid="wizard-step-publish">
          <Card className="border-emerald-500/30">
            <CardContent className="pt-4 pb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-display font-bold flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-400" /> {vehicle.specs?.marke} {vehicle.specs?.modell}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{fmtEUR(vehicle.price)} · Plattformen auswählen und veröffentlichen</div>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate(`/app/fahrzeuge/${vehicle.id}`)} data-testid="wizard-open-vehicle-button">Zum Fahrzeug</Button>
            </CardContent>
          </Card>
          <PublishTab vehicle={vehicle} reload={reloadVehicle} />
        </div>
      )}

      {/* Navigation */}
      <div className="pt-2 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-center justify-between gap-2">
          <Button variant="outline" className="gap-1 h-11" disabled={busy || Boolean(batch)}
            onClick={() => (step === 0 ? navigate(-1) : setStep(step - 1))} data-testid="wizard-back-button">
            <ChevronLeft className="h-4 w-4" /> Zurück
          </Button>
          {vehicle && step >= 2 && step < 6 && (
            <Button variant="ghost" className="text-xs text-muted-foreground h-11" onClick={() => navigate(`/app/fahrzeuge/${vehicle.id}`)} data-testid="wizard-pause-button">
              Speichern & später fortsetzen
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          {(step === 2 || step === 3) && (() => {
            const nothingDone = step === 2 ? allImages.length === 0 : !allImages.some((i) => i.processing_status === "processed");
            // Weiter erst, wenn alle Uploads abgeschlossen sind (Fotobooth braucht fertige Bilder)
            const uploadsRunning = step === 2 && uploader.active;
            return (
              <Button className="gap-1 h-12 sm:h-11 font-semibold w-full sm:w-auto" variant={nothingDone ? "outline" : "default"} disabled={Boolean(batch) || uploadsRunning}
                onClick={() => setStep(step + 1)} data-testid={nothingDone ? "wizard-skip-step-button" : "wizard-next-button"}>
                {uploadsRunning ? (<><Loader2 className="h-4 w-4 animate-spin" /> Upload läuft...</>) : (<>{nothingDone ? "Diesen Schritt überspringen" : "Weiter"} <ChevronRight className="h-4 w-4" /></>)}
              </Button>
            );
          })()}
        </div>
      </div>

      <AlertDialog open={Boolean(regenConfirm)} onOpenChange={(open) => !open && setRegenConfirm(null)}>
        <AlertDialogContent data-testid="wizard-overwrite-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Manuelle Änderungen gehen verloren</AlertDialogTitle>
            <AlertDialogDescription>
              Sie haben Inseratstexte manuell bearbeitet. Beim Neu-Generieren werden alle Textfelder
              durch neue KI-Versionen ersetzt – Ihre manuellen Anpassungen werden dabei überschrieben.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="wizard-overwrite-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { const s = regenConfirm?.style || null; setRegenConfirm(null); generate(s); }}
              data-testid="wizard-overwrite-confirm"
            >
              Trotzdem neu generieren
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const EditableBlock = ({ label, value, onSave, testId, rows = 4 }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || "");
  useEffect(() => { setVal(value || ""); }, [value]);
  return (
    <div className="rounded-lg border border-border bg-secondary/20 p-3" data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-muted-foreground">{label}</span>
        {!editing && <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setEditing(true)} data-testid={`${testId}-edit`}>Bearbeiten</Button>}
      </div>
      {editing ? (
        <div className="space-y-2">
          <Textarea rows={rows} value={val} onChange={(e) => setVal(e.target.value)} data-testid={`${testId}-textarea`} />
          <div className="flex gap-2">
            <Button size="sm" className="h-7 text-xs" onClick={() => { onSave(val); setEditing(false); }} data-testid={`${testId}-save`}>Speichern</Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setVal(value || ""); setEditing(false); }}>Abbrechen</Button>
          </div>
        </div>
      ) : (
        <p className="text-sm whitespace-pre-line">{value || "–"}</p>
      )}
    </div>
  );
};
